import { ProductSlug } from "@/api/product-api";
import { ProductUtil } from "@/utils/ProductUtil";
import { useTrans } from "@/utils/useTrans";
import { AdditionalProduct } from "@/v2-components/cart-sheet/types";
import { useFormContext } from "react-hook-form";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { useCartStore } from "@/store/cart-store";
import { getAvailableColors } from "@/utils/product-colors";
import { AccessoryItem } from "@/v2-components/cart-sheet/accessory-item";

interface Props {
  slug: ProductSlug;
  packageDeals: ProductSlug[];
  additionalProducts: AdditionalProduct[];
  updateAdditionalProducts: (additionalProducts: AdditionalProduct[]) => void;
}

export function AccessoryList({
  slug,
  packageDeals,
  additionalProducts,
  updateAdditionalProducts,
}: Props) {
  const { t } = useTrans(["gnb"]);
  const { getValues } = useFormContext();
  const { getProductsBySlug } = useCartWithProducts();
  const cartItems = useCartStore((state) => state.items);

  const glovePackageDeals = {
    small: packageDeals.filter((slug) =>
      ProductUtil.isInnerGloveSlugForSize(slug, "small"),
    ),
    medium: packageDeals.filter((slug) =>
      ProductUtil.isInnerGloveSlugForSize(slug, "medium"),
    ),
    large: packageDeals.filter((slug) =>
      ProductUtil.isInnerGloveSlugForSize(slug, "large"),
    ),
    "x-large": packageDeals.filter((slug) =>
      ProductUtil.isInnerGloveSlugForSize(slug, "x-large"),
    ),
    "all-size": packageDeals,
  };

  const packageDealProducts = getProductsBySlug(
    slug === ProductSlug.TACTGLOVE_DK2 || slug === ProductSlug.TACTGLOVE_DK3
      ? (glovePackageDeals[getValues("options").size] ?? [])
      : packageDeals,
  );

  if (packageDealProducts.length === 0) {
    return null;
  }

  return (
    <>
      <h3 className="text-2xl">{t(`Accessories`)}</h3>
      {(slug === ProductSlug.STARTER_BUNDLE ||
        slug === ProductSlug.FPS_BUNDLE ||
        slug === ProductSlug.TACTSUIT_PRO ||
        slug === ProductSlug.TACTSUIT_AIR) && (
        <p className="mt-3 rounded-lg bg-coolgray-10 p-5 text-base">
          {t(`Audio Accessories (Audio cable & Y Splitter) is included`)}
        </p>
      )}
      <ul>
        {packageDealProducts
          .flatMap((p) => {
            return p.skuOptions
              .filter((skuOption) =>
                cartItems.every((item) => item.sku !== skuOption.sku),
              )
              .map((skuOption) => ({
                ...p,
                skuOptions: [skuOption],
              }));
          })
          .map((product, index) => {
            const skuOption = product.skuOptions[0];
            const quantity =
              additionalProducts.find((item) => item.sku === skuOption.sku)
                ?.quantity ?? 1;
            const selected = additionalProducts.some(
              (item) => item.sku === skuOption.sku,
            );
            return (
              <AccessoryItem
                key={index}
                product={product}
                selected={selected}
                quantity={quantity}
                onSelect={() => {
                  const found = additionalProducts.find(
                    (item) => item.sku === skuOption.sku,
                  );

                  if (found) {
                    updateAdditionalProducts(
                      additionalProducts.filter(
                        (item) => item.sku !== skuOption.sku,
                      ),
                    );
                  } else {
                    const colors = getAvailableColors(product.skuOptions);
                    const options =
                      colors.length > 0
                        ? colors.map((color) => ({
                            type: "color",
                            value: color.color,
                          }))
                        : [];
                    updateAdditionalProducts([
                      ...additionalProducts,
                      {
                        slug: product.slug,
                        quantity: 1,
                        sku: skuOption.sku,
                        options,
                      },
                    ]);
                  }
                }}
                onQuantityChange={(quantity) => {
                  updateAdditionalProducts(
                    additionalProducts.map((item) =>
                      item.sku === skuOption.sku ? { ...item, quantity } : item,
                    ),
                  );
                }}
              />
            );
          })}
      </ul>
    </>
  );
}
